'use strict';

import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {keys} from 'ts-transformer-keys';
import {enumerate} from 'ts-transformer-enumerate';
import memoize from 'memoize-one';

import {Card} from 'primereact/card';
import {OverlayPanel} from 'primereact/overlaypanel';
import {Toast} from 'primereact/toast';

import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import {panelSchema} from '~/shared/src/validationSchemes/panel';
import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';
import {LoadParameters} from '~/shared/src/utilities/modules/queryExecute';
import {
    LoadMetaDataRowData,
    LoadMetaDataRowDataMapping
} from '~/shared/src/utilities/modules/overview';
import {
    ColumnConfig,
    ColumnDefinition
} from '~/shared/src/validationSchemes/modules/overview';
import {getDataStructureForSqlQuery} from '~/shared/src/utilities/modules/queryLoader';

import Toolbar from '~/frontend/src/components/base/QueryExecute/Toolbar';
import DataTable, {
    AllProps as DataTableAllProps
} from '~/frontend/src/components/base/DataTable';

import {getColumnConfigFromColumnDefinition} from '~/frontend/src/components/base/DataTable/utilities';
import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';

import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import * as PanelManagerActions from '~/frontend/src/actions/panelManager';
import {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import * as RequestActions from '~/frontend/src/actions/ajax';
import {StartRequest} from '~/frontend/src/actions/ajax/startRequest';
import {EndRequest} from '~/frontend/src/actions/ajax/endRequest';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {
    SetMessage,
    UnsetMessage
} from '~/frontend/src/actions/intercom';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import loadData, {LoadData} from '~/frontend/src/actions/data/loadData';


export type QueryType = 'Cypher' | 'SQL';
export const queryTypes = enumerate<QueryType>() as unknown as string[];
export const schemata = enumerate<YnSchema>() as unknown as string[];
schemata.sort();


export type DefaultProps = {
    queryType? : QueryType;
    query? : string;
    schema? : Schema;
    columns? : ColumnConfig[];
    columnDefinitions? : ColumnDefinition[];
    rowData? : LoadMetaDataRowData[];
    rowDataMapping? : LoadMetaDataRowDataMapping;
    total? : number;
};
export const defaultProps : Required<DefaultProps> = {
    queryType        : 'Cypher',
    query            : '',
    schema           : 'DATA',
    columns          : [],
    columnDefinitions: [],
    rowData          : [],
    rowDataMapping   : {},
    total            : 0
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type StateProps = {

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type SetParamsForDataTable = ( panelKey : string, panelProps : Partial<DataTableAllProps> ) => SetParamsAction;
const setParamsForDataTable = origSetParams as SetParamsForDataTable;
const Actions = Object.assign( {}, PanelManagerActions, PanelActions, RequestActions, IntercomActions, { loadData, setParamsForDataTable } ) as any;
type SetParam = <K extends keyof ( ComponentProps )>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ( ComponentProps ) ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;
type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;

type ActionPropsRaw = {
    // panel manager actions
    closePanel : ClosePanel;
    setPanelInfo : SetPanelInfo;

    // common panel actions
    setParam : SetParam;
    setParams : SetParams;
    unsetParam : UnsetParam;
    setParamsForDataTable : SetParamsForDataTable;

    // request actions
    startRequest : StartRequest;
    endRequest : EndRequest;

    // intercom actions
    setMessage : SetMessage;
    unsetMessage : UnsetMessage;

    loadData : LoadData
};
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
};

export type AllProps = Required<DefaultProps> & Props & StateProps & ActionProps;

type State = {
    errorCount : number;
    errorTooltip? : string | JSX.Element | ( string | JSX.Element )[];
    errorMessage? : string | JSX.Element | ( string | JSX.Element )[];
};


class QueryExecute extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static panelType = 'QueryExecute';
    static panelTabTitle = 'QueryExecute';
    static panelTabIcon = 'material-symbols-outlined ::database_search::';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.queryExecute';
    static panelPropsSchema = Joi.object()
        .concat( panelSchema )
        .concat( Joi.object( {
            // panelSchema defaults
            panelType    : Joi.string().valid( QueryExecute.panelType ),
            panelTabTitle: Joi.string().default( QueryExecute.panelTabTitle ),
            panelTabIcon : Joi.string().default( QueryExecute.panelTabIcon )
        } ) )
        .concat( requestBaseParametersSchema )
        .concat( Joi.object( {
            queryType: Joi.string().valid( ...queryTypes )
        } ) );

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

        };
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

        };
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

        };
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : AllProps ) : string {
        return `#${QueryExecute.panelType}/${props.queryType}`;

    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = activePanel
        if ( params?.[ 1 ] ) {
            retVal.queryType = params[ 1 ] as QueryType;
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.frontend.queryExecute.resolvePanelPropsFromURL.error.couldNotResolveQueryType' ) );
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        return retVal;
    }

    overlayPanel? : OverlayPanel | null;

    toast : Toast | null = null;
    toastTopOffset : number = 55;
    toastBottomOffset : number = 32;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            errorCount: 0
        };

        this.onChange = this.onChange.bind( this );
        this.onExecute = this.onExecute.bind( this );
        this.onQueryTypeChange = this.onQueryTypeChange.bind( this );
        this.onSchemaChange = this.onSchemaChange.bind( this );
        this.onOverlayPanelHide = this.onOverlayPanelHide.bind( this );
        this.onReload = this.onReload.bind( this );

        this.getToolbar = memoize( this.getToolbar );
        this.getDataStructure = memoize( this.getDataStructure );
        this.getDataTable = memoize( this.getDataTable );
        this.getOverlayPanel = memoize( this.getOverlayPanel );
        this.getToast = memoize( this.getToast );
        this.getTextarea = memoize( this.getTextarea );
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.afterRender();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : AllProps ) {
        this.afterRender();
    }


    /* event handler **********************************************************************************************************************/

    onChange( event : React.ChangeEvent<HTMLTextAreaElement> ) {
        this.props.setParam( this.props.uid, 'query', event.target.value );
    }

    async onExecute( start? : number, limit? : number ) {
        const {
                  uid,
                  queryType,
                  query,
                  schema
              }                       = this.props,
              params : LoadParameters = {
                  queryType,
                  schema,
                  query,
                  start,
                  limit
              };

        try {
            this.props.startRequest(
                () => {
                },
                uid,
                'processing'
            );

            await this.props.loadData(
                'queryExecute/load',
                uid,
                uid,
                'load',
                schema,
                {
                    params
                },
                false,
                ( result ) => {
                    const paramsToSet : Partial<AllProps>        = {},
                          columnDefinitions : ColumnDefinition[] = [],
                          columns : ColumnConfig[]               = [];

                    for ( const columnDataDescriptor of result.data.columnDataDescriptors ) {
                        columnDefinitions.push( {
                            key           : columnDataDescriptor.alias,
                            field         : columnDataDescriptor.alias,
                            header        : columnDataDescriptor.alias,
                            dataDescriptor: columnDataDescriptor,
                            editor        : false,
                            filter        : false,
                            valueType     : columnDataDescriptor.valueType ?? 'STRING',
                            hidable       : false,
                            hidden        : false,
                            searchColumn  : false,
                            sortable      : false,
                            width         : 100
                        } );
                        columns.push( getColumnConfigFromColumnDefinition( columnDefinitions[ columnDefinitions.length - 1 ] ) );
                    }

                    if ( JSON.stringify( columns ) !== JSON.stringify( this.props.columns ) ) {
                        paramsToSet.columns = columns;
                    }
                    if ( JSON.stringify( columnDefinitions ) !== JSON.stringify( this.props.columnDefinitions ) ) {
                        paramsToSet.columnDefinitions = columnDefinitions;
                    }
                    if ( JSON.stringify( result.metaData.rowData ) !== JSON.stringify( this.props.rowData ) ) {
                        paramsToSet.rowData = result.metaData.rowData;
                    }
                    if ( JSON.stringify( result.metaData.rowDataMapping ) !== JSON.stringify( this.props.rowDataMapping ) ) {
                        paramsToSet.rowDataMapping = result.metaData.rowDataMapping;
                    }
                    if ( result.metaData.total !== this.props.total ) {
                        paramsToSet.total = result.metaData.total;
                    }

                    if ( Object.keys( paramsToSet ).length ) {
                        this.props.setParams( uid, paramsToSet );
                    }

                    if ( !start ) {
                        this.props.setParamsForDataTable( `${uid}_datatable`, {
                            columns          : [],
                            columnDefinitions: [],
                            page             : 1
                        } );
                    }

                    this.overlayPanel?.show( null, document.body );
                }
            );

            this.props.endRequest(
                () => {
                },
                uid,
                'processing'
            );
        }
        catch ( e ) {
            console.error( e );
        }
    }

    onQueryTypeChange( queryType : DefaultProps[ 'queryType' ] ) {
        this.props.setParam( this.props.uid, 'queryType', queryType );
    }

    onSchemaChange( schema : DefaultProps[ 'schema' ] ) {
        this.props.setParam( this.props.uid, 'schema', schema );
    }

    onOverlayPanelHide() {
        this.props.setParams( this.props.uid, {
            rowData       : undefined,
            rowDataMapping: undefined,
            total         : undefined
        } );
    }

    onReload( props : DataTableAllProps ) {
        void this.onExecute( ( props.page - 1 ) * props.rows, props.rows );

        return true;
    }


    /* private ****************************************************************************************************************************/

    afterRender() {
        const {
                  uid
              }                     = this.props,
              {
                  errorCount
              }                     = this.state,
              toolbar               = document.getElementById( ( `${uid}_toolbar` ) ) as HTMLElement,
              toolbarHeight         = toolbar?.offsetHeight ?? 0,
              dataTable             = document.getElementById( `${uid}_datatable` ) as HTMLElement,
              dataTableHeader       = dataTable
                  ? dataTable.querySelector( '.p-datatable-thead' ) as HTMLElement
                  : null,
              dataTableHeaderHeight = dataTableHeader?.offsetHeight ?? 0,
              toastTopOffset        = toolbarHeight + dataTableHeaderHeight,
              dataTablePagination   = dataTable
                  ? dataTable.querySelector( '.p-paginator' ) as HTMLElement
                  : null,
              toastBottomOffset     = dataTablePagination?.offsetHeight ?? 0;

        let tooltip = `<div className="yn-tooltip-header">${QueryExecute.panelTabTitle}</div>`;

        if ( toastTopOffset ) {
            this.toastTopOffset = toastTopOffset;
        }
        if ( toastBottomOffset ) {
            this.toastBottomOffset = toastBottomOffset;
        }

        this.checkForIntercomMessages( this.props.intercomMessages );

        // error
        if ( errorCount ) {
            tooltip += `<div className="yn-tooltip-info mb-0">
                            <div className="yn-tooltip-info-title" style="border-bottom: none; padding-bottom: 0; margin-bottom: 0;"><span className="fas fa-exclamation-circle yn-error mr-2"></span> ${__( 'YN.base.panel.tooltip.hasError' )}</div>
                        </div>`;
        }

        this.props.setPanelInfo( uid, {
            title  : QueryExecute.panelTabTitle,
            icon   : QueryExecute.panelTabIcon,
            tooltip,
            dirty  : false,//( activePanel === 'input' && !!this.inputPanel?.getXml() ),
            invalid: !!errorCount,
            path   : QueryExecute.buildPanelPathFromProps( this.props )
        } );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage( this.props.uid, 'toast', key as string );
            }
        }
    }

    getToolbar( queryType : AllProps['queryType'], schema : AllProps['schema'], query : AllProps['query'], user : AllProps['user'] ) {
        const {
                  uid
              } = this.props

        return <Toolbar
            key={`${uid}_toolbar`}

            uid={`${uid}_toolbar`}

            showQueryTypeButtons={user?.isMemberOfGroup( config.sysGroups?.admin ?? '' )}
            queryType={queryType}
            onQueryTypeChange={this.onQueryTypeChange}

            schema={schema}
            onSchemaChange={this.onSchemaChange}

            showExecuteButton={true}
            executeButtonDisabled={!( query.trim() )}
            onExecute={() => {
                void this.onExecute()
            }}
        />;
    }

    getDataStructure() {
        return getDataStructureForSqlQuery();
    }

    getDataTable( columnDefinitions : ColumnDefinition[], columns : ColumnConfig[], rowData : LoadMetaDataRowData[], rowDataMapping : LoadMetaDataRowDataMapping, total : number, dataTableProps : string ) {
        const {
                  uid
              }                    = this.props,
              dataStructure        = this.getDataStructure(),
              dataTablePropsParsed = JSON.parse( dataTableProps );

        return <DataTable
            key={`${uid}_datatable`}

            {...dataTablePropsParsed}

            uid={`${uid}_datatable`}
            dataContext={`${uid}_datatable`}
            dataStructure={dataStructure}
            columns={dataTablePropsParsed.columns?.length ? dataTablePropsParsed.columns : columns}
            columnDefinitions={dataTablePropsParsed.columnDefinitions?.length ? dataTablePropsParsed.columnDefinitions : columnDefinitions}
            value={rowData}
            valueFieldMapping={rowDataMapping}
            totalRecords={total}
            filterable={false}
            sortable={false}
            reload={this.onReload}
            className="yn-datatable-full yn-flex yn-datatable-border"
        />;
    }

    getOverlayPanel( columnDefinitions : ColumnDefinition[], columns : ColumnConfig[], rowData : LoadMetaDataRowData[], rowDataMapping : LoadMetaDataRowDataMapping, total : number, dataTableProps : string ) {
        const {
                  uid
              }         = this.props,
              dataTable = this.getDataTable( columnDefinitions, columns, rowData, rowDataMapping, total, dataTableProps );

        return <OverlayPanel
            key={`${uid}_overlayPanel`}

            ref={el => this.overlayPanel = el}

            appendTo={document.body}
            title={__( 'YN.base.frontend.queryExecute.result.title' )}
            dismissable={true}
            showCloseIcon={true}
            onHide={this.onOverlayPanelHide}
            className="yn-queryexecute-result-overlay"
            pt={{
                content: {
                    className: 'yn-flex-layout-parent',
                    style    : {
                        position: 'relative'
                    }
                }
            }}
        >
            {dataTable}
        </OverlayPanel>;
    }

    getToast() {
        const {
                  uid
              } = this.props;

        return <Toast
            key={`${uid}_toast`}

            ref={( el ) => this.toast = el}

            position="bottom-right"
            style={{
                position    : 'absolute',
                marginTop   : this.toastTopOffset,
                marginBottom: this.toastBottomOffset
            }}
        />;
    }

    getTextarea( query : string ) {
        const {
                  uid
              } = this.props;

        return <div className="yn-flex-layout-parent yn-flex-layout-child">
            <div className="yn-flex-layout-parent yn-flex-layout-child flex-row align-items-stretch">
                <div key={`${uid}_inputTextarea_panel`} className="yn-content-panel-container yn-flex-layout-parent"
                     style={{ flex: '1 1 auto' }}>
                    <Card className="yn-flex-layout-parent" style={{ flex: '1 1 auto' }}>
                            <textarea
                                key={`${uid}_input`}

                                id={`${uid}_input`}

                                onChange={this.onChange}
                                value={query}

                                className="yn-query yn-editable"
                                style={{ flex: 1, overflowX: 'hidden', overflowY: 'auto' }}
                            />
                    </Card>
                </div>
            </div>
        </div>;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  columnDefinitions,
                  columns,
                  query,
                  queryType,
                  rowData,
                  rowDataMapping,
                  schema,
                  total,
                  uid,
                  user
              }              = this.props,
              dataTableProps = getSubPanelProps( `${uid}_datatable` );

        return <>
            {this.getToast()}
            {this.getToolbar( queryType, schema, query, user )}
            {this.getOverlayPanel( columnDefinitions, columns, rowData, rowDataMapping, total, JSON.stringify( dataTableProps ) )}
            {this.getTextarea( query )}
        </>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
              xhrRequestsForComponent: undefined,
              intercomMessages       : undefined,
              user                   : undefined
          },
          {
              uid
          }                   = ownProps;

    // get the running xhr request belonging to this component (uid)
    if ( state.xhr?.[ uid ] ) {
        retVal.xhrRequestsForComponent = state.xhr[ uid ];
    }

    // get intercom messages
    if ( state.intercom?.[ uid ] ) {
        retVal.intercomMessages = state.intercom[ uid ];
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( QueryExecute );
